import { useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type { DocumentReference, Provenance, QuestionnaireResponse } from '@medplum/fhirtypes'
import { logError } from '@/utils/errorUtils'

function extractAnswer(qr: QuestionnaireResponse, linkId: string): string {
  const queue = [...(qr.item ?? [])]
  while (queue.length > 0) {
    const item = queue.shift()!
    if (item.linkId === linkId) return item.answer?.[0]?.valueString ?? ''
    if (item.item?.length) queue.push(...item.item)
  }
  return ''
}

export function useLastProgressNoteIndication(patientId: string | undefined) {
  const medplum = useMedplum()
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!patientId) return
    let active = true

    const run = async () => {
      setLoading(true)
      try {
        const docBundle = await medplum.search('DocumentReference', {
          subject: `Patient/${patientId}`,
          // Code-only token: authored notes moved from the healthgorilla to the
          // behuman document-type system (37cc57ad), and imported HG notes must
          // keep matching — never qualify progress-note searches by system.
          type: 'ProgressNote',
          'status:not': 'entered-in-error',
          _sort: '-date',
          // The newest note may be imported (imported notes never have a
          // QR-backed Provenance) — walk back to the last authored note.
          _count: '5',
        })
        const docRefs = (docBundle.entry ?? [])
          .map((entry) => entry.resource as DocumentReference | undefined)
          .filter((doc): doc is DocumentReference & { id: string } => Boolean(doc?.id))
        if (docRefs.length === 0 || !active) return

        const provBundle = await medplum.search('Provenance', {
          target: docRefs.map((doc) => `DocumentReference/${doc.id}`).join(','),
          _count: '100',
        })
        if (!active) return

        // A note accrues several Provenances (draft-saves, clinical cascade,
        // lock attestation) in no guaranteed order; only draft-saves carry the
        // QR target — scan all of them, newest note first.
        const provenances = (provBundle.entry ?? [])
          .map((entry) => entry.resource as Provenance | undefined)
          .filter((prov): prov is Provenance => Boolean(prov))

        let qrRef: string | undefined
        for (const doc of docRefs) {
          qrRef = provenances
            .filter((prov) =>
              prov.target?.some((t) => t.reference === `DocumentReference/${doc.id}`),
            )
            .flatMap((prov) => prov.target ?? [])
            .map((t) => t.reference)
            .find((r) => r?.startsWith('QuestionnaireResponse/'))
          if (qrRef) break
        }
        if (!qrRef || !active) return

        const qr = await medplum.readResource('QuestionnaireResponse', qrRef.split('/')[1])
        if (!active) return

        const subjective = extractAnswer(qr, 'prob')
        const objective = extractAnswer(qr, 'objective')
        const assessment = extractAnswer(qr, 'assessment')
        const plan = extractAnswer(qr, 'plan')
        const text = [subjective, objective, assessment, plan].filter(Boolean).join('\n\n')
        setSuggestion(text || null)
      } catch (err) {
        logError('referral', 'Failed to fetch last progress note indication', err)
      } finally {
        if (active) setLoading(false)
      }
    }

    run()
    return () => {
      active = false
    }
  }, [patientId, medplum])

  return { suggestion, loading }
}
